import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { getProjects } from "@/lib/api";
import { useLanguage } from "@/i18n/LanguageContext";

const FALLBACK_IMG = "https://images.unsplash.com/photo-1719650592946-55163c4994cb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwyfHxuZXVyYWwlMjBuZXR3b3JrJTIwZ2xvd2luZyUyMGNvbm5lY3Rpb25zfGVufDB8fHx8MTc5MjYwMTAyOHww&ixlib=rb-4.1.0&q=85";

const Projects = () => {
  const { t, lang } = useLanguage();
  const pick = (p, f) => (lang !== "it" ? p[`${f}_${lang}`] || p[`${f}_en`] || p[f] : p[f]);
  const pickTags = (p) => (lang !== "it" && p.tags_en && p.tags_en.length ? p.tags_en : p.tags);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    getProjects()
      .then((data) => setProjects(data))
      .catch(() => setProjects([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section id="progetti" className="py-24 lg:py-32" data-testid="projects-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mb-16"
        >
          <p className="text-xs font-bold tracking-[0.3em] uppercase text-neon-cyan mb-4">{t.projects.overline}</p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight">
            {t.projects.titleA} <span className="neon-text">{t.projects.titleNeon}</span>
          </h2>
          <p className="mt-6 text-base lg:text-lg text-white/70">
            {t.projects.subtitle}
          </p>
        </motion.div>

        {loading && (
          <p className="text-white/50 text-sm" data-testid="projects-loading">{t.projects.loading}</p>
        )}

        {!loading && projects.length === 0 && (
          <p className="text-white/50 text-sm" data-testid="projects-empty">
            {t.projects.empty}
          </p>
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {projects.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.12 }}
              data-testid={`project-card-${p.id}`}
              className="glass-panel spotlight rounded-2xl overflow-hidden hover:-translate-y-2 hover:shadow-[0_0_40px_rgba(0,240,255,0.12)] transition-all duration-500 group flex flex-col"
            >
              <div className="h-52 overflow-hidden relative">
                <img
                  src={p.image_url || FALLBACK_IMG}
                  alt={`Progetto ${p.title} — XS Consulting`}
                  onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  className="w-full h-full object-cover opacity-70 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#08080c] to-transparent" />
                <span className="absolute top-4 left-4 text-xs font-bold tracking-widest uppercase border border-neon-cyan/40 text-neon-cyan rounded-full px-3 py-1 bg-black/40 backdrop-blur">
                  {pick(p, "category")}
                </span>
              </div>
              <div className="p-7 flex flex-col flex-1">
                <h3 className="font-display text-xl font-bold mb-3 leading-snug">{pick(p, "title")}</h3>
                <p className="text-sm text-white/70 leading-relaxed mb-5">{pick(p, "description")}</p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {pickTags(p).map((tag) => (
                    <span key={tag} className="text-xs border border-white/10 rounded-full px-3 py-1 text-white/60">{tag}</span>
                  ))}
                </div>
                <button
                  onClick={() => setExpanded(expanded === p.id ? null : p.id)}
                  data-testid={`project-presentation-toggle-${p.id}`}
                  className="mt-auto flex items-center gap-2 text-sm font-semibold text-neon-cyan hover:gap-3 transition-all duration-300"
                >
                  {expanded === p.id ? t.projects.close : t.projects.open}
                  {expanded === p.id ? <ChevronDown size={16} /> : <ArrowUpRight size={16} />}
                </button>
                <AnimatePresence>
                  {expanded === p.id && pick(p, "presentation") && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.4 }}
                      className="overflow-hidden"
                    >
                      <p className="pt-5 mt-5 border-t border-white/10 text-sm text-white/80 leading-relaxed" data-testid={`project-presentation-${p.id}`}>
                        {pick(p, "presentation")}
                      </p>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Projects;
